import "./styles.scss";

import { formatChatTime, formatDateToLong } from "../../utils/helpers";
import { useEffect, useState } from "react";

import { API_URL } from "../../utils/constants";
import AlertMessage from "../lessons/trial/AlertMessage";
import { AppLoader } from "../../components/AppLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../layout/Portal";
import { placeholder } from "../../constants/images";
import { socket } from "../../socket";
import { useMessages } from "../../hooks/useMessages";
import { useTrialLesson } from "../../context/TrailLessonContext";
import { v4 as uuidv4 } from "uuid";

export const Message = ({ isConnected }: { isConnected: boolean }) => {
  const { isTrialLessonCreated } = useTrialLesson();
  const [viewType, setViewType] = useState<"notifications" | "messages">(
    "notifications",
  );
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [msg, setMsg] = useState("");
  const [userId, setUserId] = useState<string>("");

  const [notifications, setNotifications] = useState({
    loading: false,
    data: [],
    error: "",
  });
  const [messageList, setMessageList] = useState({
    loading: false,
    data: [],
    error: "",
  });

  const { messages, loading, addMessage, updateMessageStatus, fetchChatList } =
    useMessages();

  useEffect(() => {
    const id = localStorage.getItem("id");
    if (id) setUserId(id);

    const controller = new AbortController();
    fetchNotifications(controller.signal);
    fetchMessageList(controller.signal);

    return () => controller.abort();
  }, []);

  const fetchNotifications = async (signal: AbortSignal) => {
    const token = localStorage.getItem("token");
    try {
      setNotifications({ ...notifications, loading: true });
      const res = await fetch(`${API_URL}/notification`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      const json = await res.json();
      setNotifications({
        data: json?.result?.notifications || [],
        loading: false,
        error: "",
      });
    } catch (err: any) {
      setNotifications({
        ...notifications,
        loading: false,
        error: err.message,
      });
    }
  };

  const fetchMessageList = async (signal: AbortSignal) => {
    const token = localStorage.getItem("token");
    try {
      setMessageList({ ...messageList, loading: true });
      const res = await fetch(`${API_URL}/messages/merchants`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      const json = await res.json();
      setMessageList({
        data: json?.result || [],
        loading: false,
        error: "",
      });
    } catch (err: any) {
      setMessageList({ ...messageList, loading: false, error: err.message });
    }
  };

  const onSend = () => {
    if (!msg.trim() || !selectedItem?.userId || !userId)
      return alert("Type a message");
    if (!isConnected) return alert("Socket disconnected.");

    const transaction = uuidv4();
    const newMessage = {
      transaction,
      content: msg,
      senderUserType: "teacher",
      receiver: selectedItem.userId,
      createdAt: new Date().toISOString(),
      status: "pending",
    };

    addMessage(newMessage);
    setMsg("");

    socket.emit("sendMessage", {
      transaction,
      content: msg,
      senderUserType: "teacher",
      receiverId: selectedItem.userId,
      createdAt: new Date(),
    });

    socket.once("messageSent", (res) =>
      updateMessageStatus(transaction, res ? "sent" : "failed"),
    );
    socket.once("errorOccurred", () =>
      updateMessageStatus(transaction, "failed"),
    );
  };

  socket.off("newMessage").on("newMessage", (res: any) => addMessage(res));

  return (
    <Layout color="transparent">
      <div className="" style={{ marginBottom: "2rem" }}>
        {!isTrialLessonCreated && <AlertMessage title="Action Required" />}
      </div>
      <div className="container">
        <div className="mainContainer">
          <div className="mainTop">
            {["notifications", "messages"].map((tab) => (
              <span
                key={tab}
                onClick={() => {
                  setSelectedItem(null);
                  setViewType(tab as "notifications" | "messages");
                }}
                className={viewType === tab ? "topTextPicked" : "topText"}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </span>
            ))}
          </div>

          <div className="mappedContent">
            {viewType === "notifications" ? (
              notifications.loading ? (
                <AppLoader />
              ) : notifications.data.length === 0 ? (
                <div className="noData">No notifications</div>
              ) : (
                notifications.data.map((item: any, i: number) => (
                  <div
                    key={i}
                    className={`mapItemContainer ${
                      selectedItem === item ? "active" : ""
                    }`}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="inner">
                      <img
                        src={item.image || placeholder}
                        className="image"
                        alt="avatar"
                      />
                      <div>
                        <span className="name">{item.name}</span>
                        <span className="content">{item.content}</span>
                      </div>
                    </div>
                    <p className="date">{formatDateToLong(item.date)}</p>
                  </div>
                ))
              )
            ) : messageList.loading ? (
              <AppLoader />
            ) : messageList.data.length === 0 ? (
              <div className="noData">No messages</div>
            ) : (
              messageList.data.map((item: any, i: number) => (
                <div
                  key={i}
                  onClick={() => {
                    fetchChatList(item.userId);
                    setSelectedItem(item);
                  }}
                  className="mapItemContainer"
                  style={{
                    backgroundColor:
                      item?.userId === selectedItem?.userId
                        ? "#b7e1f9"
                        : "white",
                  }}
                >
                  <div className="inner">
                    <img
                      src={item.profilePicture || placeholder}
                      className="image"
                      alt="profile"
                    />
                    <span className="name">{`${item.firstName} ${item.lastName}`}</span>
                  </div>
                  <div className="messageContent">
                    <span className="content">
                      {item?.lastMessage?.content}
                    </span>
                  </div>
                  <span className="date">
                    {formatDateToLong(item?.lastMessage?.createdAt)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {selectedItem && (
          <div className="detailContainer">
            <div className="header">
              <div className="name">
                {viewType === "notifications"
                  ? selectedItem.name
                  : `${selectedItem.firstName} ${selectedItem.lastName}`}
              </div>
              {/* <div
                className="content close"
                onClick={() => setSelectedItem(null)}
              >
                Close
              </div> */}
            </div>

            {viewType === "notifications" ? (
              <div className="notContent">
                <span className="dateNote">
                  {formatDateToLong(selectedItem.date)}
                </span>
                <img
                  src={selectedItem.image || placeholder}
                  className="imageNot"
                  alt="notification"
                />
                <span className="fullContent">{selectedItem.content}</span>
              </div>
            ) : (
              <>
                <div className="messagesContainer">
                  {loading ? (
                    <AppLoader />
                  ) : messages.length === 0 ? (
                    <span className="noData">No messages</span>
                  ) : (
                    messages.map((msg: any, i: number) => (
                      <div key={i} className="chatsCon">
                        <img
                          src={
                            msg.senderUserType === "user"
                              ? selectedItem.profilePicture || placeholder
                              : placeholder
                          }
                          className="image"
                          alt="avatar"
                        />
                        <div>
                          <div className="innerChat">
                            <span className="chatName">
                              {msg.senderUserType === "teacher"
                                ? "You"
                                : `${selectedItem.firstName} ${selectedItem.lastName}`}
                            </span>
                            <span className="chatDate">
                              {formatChatTime(msg.createdAt)}
                            </span>
                          </div>
                          <span className="chatContent">{msg.content}</span>
                          <br />
                          {msg.senderUserType !== "teacher"
                            ? null
                            : msg.status && (
                                <FontAwesomeIcon
                                  icon={
                                    msg.status === "pending"
                                      ? ["fas", "clock"]
                                      : msg.status === "sent"
                                        ? ["fas", "check"]
                                        : ["fas", "circle-notch"]
                                  }
                                />
                              )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="inputCon">
                  <input
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    className="inputChat"
                    placeholder="Type a message..."
                  />
                  <FontAwesomeIcon
                    onClick={onSend}
                    className="icon"
                    icon={["far", "paper-plane"]}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};
